<template>
  <signup-layout>
    <p class="header-text">Create Your Account</p>
    <img
      class="-ml-3 w-full"
      src="@/assets/tracker-signup.svg"
      alt="sign up tracker"
    />
    <form
      v-if="needsName || needsEmail"
      novalidate="novalidate"
      @submit.prevent="handleNameSubmit"
    >
      <p class="mt-6 text-1lg text-dark-blue">Enter Name</p>
      <div>
        <p class="my-6 text-left text-dark-gray sm:my-8">
          Let’s get some basic information to complete your account.
        </p>

        <form-field
          v-if="needsName"
          label="Full Name"
          :value="full_name"
          placeholder="Enter your full name"
          :on-input="handleFieldUpdate"
          :on-blur="checkFullname"
          :error="full_name_error"
          :alert-message="full_name_alert"
          name="full_name"
        />
        <form-field
          v-if="needsEmail"
          ref="emailField"
          label="Company Email"
          :value="email_address"
          placeholder="Enter your company email"
          :on-input="handleFieldUpdate"
          :on-blur="checkEmailAddress"
          :error="email_address_error"
          name="email_address"
        />
        <button-main
          class="mb-4 mt-3 sm:mt-6"
          :disabled="isDisabledBtn"
          type="submit"
          value="Continue"
        />
      </div>
    </form>
    <div>
      <form
        v-if="!needsName && needsPhone && !isEmailRequiredForLogin"
        novalidate="novalidate"
        @submit.prevent="handlePhoneSubmit"
      >
        <div class="text-left">
          <p class="mt-6 text-1lg text-dark-blue">Enter Mobile Number</p>
          <p class="my-6 text-left text-dark-gray sm:my-8">
            In order to protect your account, we need to verify your mobile
            number.
          </p>
          <form-field
            ref="formField"
            :auto-focus="true"
            label="Mobile Phone #"
            :value="input_phone_number"
            placeholder="Enter Mobile #"
            :on-input="onPhoneInput"
            name="phone_number"
            type="tel"
            :on-blur="validatePhone"
            :error="phoneError"
            pattern="number"
          />
          <button-main
            class="my-3 mb-4 sm:mt-6"
            :disabled="input_phone_number?.length !== 12 || !!phoneError"
            type="submit"
            value="Continue"
          />
        </div>
      </form>
    </div>
  </signup-layout>
  <!-- Phone Form -->
</template>

<script type="text/javascript">
import { mapState } from 'vuex';

import { toastResponse } from '@/common/handleServerResponse';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import SignupLayout from '@/components/layouts/SignupLayout.vue';

//Mixin
import fullnameAndEmailField from '@/mixins/fullnameAndEmailField';
import phoneField from '@/mixins/phoneField';

//Validators
import { formatFirstLastName, formatPhone } from '@/common/validators';

export default {
  name: 'My Profile',
  components: {
    ButtonMain,
    FormField,
    SignupLayout,
  },
  mixins: [fullnameAndEmailField, phoneField],
  data() {
    return {
      input_phone_number: null,
      input_phone_number_with_country_code: null,
      steps: {
        name: '',
        mobile: '',
        verifications: '',
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.users.currentUser;
    },
    needsPhone() {
      return this.$store.state.users.currentUser?.phone_e164 == null;
    },
    needsName() {
      return this.$store.state.users.currentUser?.first_name == null;
    },
    needsEmail() {
      return this.$store.state.users.currentUser?.email == null;
    },
    isDisabledBtn() {
      let full_name_validation = false;
      let email_address_validation = false;
      if (this.needsName) {
        full_name_validation = !this.full_name || this.full_name_error_sw != '';
      }
      if (this.needsEmail) {
        email_address_validation =
          !this.email_address || this.email_address_error != '';
      }
      return full_name_validation || email_address_validation;
    },
    ...mapState({
      isEmailRequiredForLogin: (state) =>
        state.carriers?.isEmailRequiredForLogin || false,
    }),
  },
  methods: {
    onPhoneInput(e) {
      const value = formatPhone(e.target.value);
      if (value.length === 12) this.validatePhone(e);
      this.input_phone_number = value;
      this.input_phone_number_with_country_code =
        this.$refs?.formField?.elementData?.getNumber();

      this.$store.commit('updateSignupField', {
        name: 'phone_number',
        value: this.input_phone_number_with_country_code,
      });
    },
    async handleNameSubmit() {
      const body = {};

      if (this.needsName) {
        // this.checkFullname();
        if (this.full_name_error_sw) return;
        const { first_name, last_name } = formatFirstLastName(this.full_name);
        body.first_name = first_name;
        body.last_name = last_name;
      }

      if (this.needsEmail) {
        const emailField = this.$refs.emailField;
        this.checkEmailAddress(emailField.value);
        if (this.email_address_error) return;
        body.email = this.email_address;
      }

      const userRes = await this.$store.dispatch(
        'users/patchSignUpDetails',
        body
      );

      if ([200, 201].includes(userRes.status)) {
        // Success updating user information
        this.$store.dispatch('users/serverRefreshCurrentUser');
        if (!this.needsPhone) {
          let redirectAfterSignIn = JSON.parse(
            localStorage.getItem('redirect_url_after_sigin')
          );

          // If they came in through the OAuth process,
          // then then they will be redirected to the /oauth/authorizations
          // endpoint with the correct credentials
          if (redirectAfterSignIn) {
            console.log('redirect user redirect_url_after_sigin');
            localStorage.removeItem('redirect_url_after_sigin');
            return this.$router.push(redirectAfterSignIn);
          } else {
            let companyType =
              this.$store.state.users.currentUser.company.company_type;

            switch (companyType) {
              case 'broker':
                return this.$router.push({ name: 'Broker Dashboard' });
              case 'carrier':
                return this.$router.push({ name: 'Carrier Dashboard' });
              case 'dispatcher':
                return this.$router.push({ name: 'Dispatcher Dashboard' });
              default:
                return this.$router.push({ name: 'Home' });
            }
          }
        } else if (this.isEmailRequiredForLogin) {
          let redirectAfterSignIn = JSON.parse(
            localStorage.getItem('redirect_url_after_sigin')
          );

          // If they came in through the OAuth process,
          // then then they will be redirected to the /oauth/authorizations
          // endpoint with the correct credentials
          if (redirectAfterSignIn) {
            localStorage.removeItem('redirect_url_after_sigin');
            return this.$router.push(redirectAfterSignIn);
          } else {
            let companyType =
              this.$store.state.users.currentUser.company.company_type;

            switch (companyType) {
              case 'broker':
                return this.$router.push({ name: 'Broker Dashboard' });
              case 'carrier':
                return this.$router.push({ name: 'Carrier Dashboard' });
              case 'dispatcher':
                return this.$router.push({ name: 'Dispatcher Dashboard' });
              default:
                return this.$router.push({ name: 'Home' });
            }
          }
        }
      } else {
        // handle error for updating user details
        toastResponse(this, userRes, null);
      }
    },
    async handlePhoneSubmit() {
      const res = await this.$store.dispatch(
        'verifications/createVerification',
        this.input_phone_number_with_country_code
      );

      if ([200, 201].includes(res.status)) {
        // phone number login success
        // send user to login verification step
        this.$router.push({ name: 'Carrier Claim My Profile Verification' });
      } else {
        // Phone number does not exist -- prompt to create an account
        toastResponse(this, res, null);
      }
    },
  },
};
</script>
<style scoped>
.header-text {
  font-size: 26px;
  @apply mb-6 text-dark-blue md:mb-8;
}
</style>
